import Cookies from "universal-cookie";

const CONSENT_COOKIE_NAME = 'cookie_consent_preferences';
const CONSENT_VERSION = '1.0';

/**
 * Cookie categories with translation keys
 * The actual text will be loaded from translation files
 */
export const COOKIE_CATEGORIES = {
  necessary: {
    id: 'necessary',
    nameKey: 'CookieConsent.Categories.Necessary.Title',
    descriptionKey: 'CookieConsent.Categories.Necessary.Description',
    required: true
  },
  analytics: {
    id: 'analytics',
    nameKey: 'CookieConsent.Categories.Analytics.Title',
    descriptionKey: 'CookieConsent.Categories.Analytics.Description',
    required: false
  },
  functional: {
    id: 'functional',
    nameKey: 'CookieConsent.Categories.Functional.Title',
    descriptionKey: 'CookieConsent.Categories.Functional.Description',
    required: false
  },
  marketing: {
    id: 'marketing',
    nameKey: 'CookieConsent.Categories.Marketing.Title',
    descriptionKey: 'CookieConsent.Categories.Marketing.Description',
    required: false
  }
};

/**
 * Default consent state
 */
const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  functional: false,
  marketing: false,
  version: CONSENT_VERSION,
  timestamp: null,
  lastUpdated: null
};

/**
 * Get current consent preferences
 */
export function getConsentPreferences() {
  const cookies = new Cookies();
  const savedConsent = cookies.get(CONSENT_COOKIE_NAME);
  
  if (!savedConsent) {
    return DEFAULT_CONSENT;
  }
  
  // Check if consent version matches
  if (savedConsent.version !== CONSENT_VERSION) {
    // Version mismatch, return default to force re-consent
    return DEFAULT_CONSENT;
  }
  
  return {
    ...DEFAULT_CONSENT,
    ...savedConsent
  };
}

/**
 * Save consent preferences
 */
export function saveConsentPreferences(preferences) {
  const cookies = new Cookies();
  const consentData = {
    ...preferences,
    version: CONSENT_VERSION,
    lastUpdated: new Date().toISOString()
  };
  
  if (!consentData.timestamp) {
    consentData.timestamp = new Date().toISOString();
  }
  
  const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN;
  if (cookieDomain) {
    // Drop any older host-only copy, otherwise it shadows the shared one
    cookies.remove(CONSENT_COOKIE_NAME, { path: '/' });
  }

  // Shared on the parent domain with the Laravel landing pages (be-teampulse/resources/js/analytics.js)
  cookies.set(CONSENT_COOKIE_NAME, consentData, {
    path: '/',
    domain: cookieDomain || undefined,
    maxAge: 365 * 24 * 60 * 60, // 1 year
    sameSite: 'strict',
    secure: window.location.protocol === 'https:'
  });
  
  // Trigger consent update event
  window.dispatchEvent(new CustomEvent('consentUpdated', { detail: consentData }));
  
  return consentData;
}

/**
 * Check if a specific category has consent
 */
export function hasConsentForCategory(category) {
  const consent = getConsentPreferences();
  return consent[category] === true;
}

/**
 * Check if user has made any consent choice
 */
export function hasUserConsented() {
  const consent = getConsentPreferences();
  return consent.timestamp !== null;
}

/**
 * Accept all cookies
 */
export function acceptAllCookies() {
  const preferences = {
    necessary: true,
    analytics: true,
    functional: true,
    marketing: true
  };
  return saveConsentPreferences(preferences);
}

/**
 * Reject all non-necessary cookies
 */
export function rejectAllCookies() {
  const preferences = {
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false
  };
  return saveConsentPreferences(preferences);
}

/**
 * Define which cookies belong to which categories
 */
const COOKIE_MAPPINGS = {
  necessary: [
    'token',
    'user_id', 
    'team_uuid',
    'credits',
    'cookie_consent_preferences'
  ],
  analytics: [
    'analytics_session_id',
    'analytics_consent',
    '_ga',
    '_ga_*',
    '_gid',
    '_gat',
    '_gat_*'
  ],
  functional: [
    'bannerDismissed',
    'language',
    'theme'
  ],
  localStorage: [
    'language',
    'i18nextLng'
  ],
  marketing: [
    // Add marketing cookie names here if any
  ]
};

/**
 * Clean up cookies based on current consent
 */
export function cleanupCookiesBasedOnConsent() {
  const consent = getConsentPreferences();
  const cookies = new Cookies();
  
  // Clean up analytics cookies if consent is withdrawn
  if (!consent.analytics) {
    COOKIE_MAPPINGS.analytics.forEach(cookieName => {
      if (cookieName.includes('*')) {
        // Handle wildcard patterns like _ga_*
        const pattern = cookieName.replace('*', '');
        // Note: This is a simplified cleanup - in production you might need
        // more sophisticated pattern matching
        Object.keys(document.cookie.split(';')).forEach(key => {
          if (key.trim().startsWith(pattern)) {
            cookies.remove(key.trim(), { path: '/' });
          }
        });
      } else {
        cookies.remove(cookieName, { path: '/' });
      }
    });
  }
  
  // Clean up functional cookies if consent is withdrawn
  if (!consent.functional) {
    COOKIE_MAPPINGS.functional.forEach(cookieName => {
      cookies.remove(cookieName, { path: '/' });
    });
    
    // Clean up localStorage items for functional data
    COOKIE_MAPPINGS.localStorage.forEach(itemName => {
      localStorage.removeItem(itemName);
    });
  }
  
  // Clean up marketing cookies if consent is withdrawn
  if (!consent.marketing) {
    COOKIE_MAPPINGS.marketing.forEach(cookieName => {
      cookies.remove(cookieName, { path: '/' });
    });
  }
  
  console.log('Cookie cleanup completed based on consent preferences');
}

/**
 * Check if a specific cookie is allowed based on its category
 */
export function isCookieAllowed(cookieName) {
  const consent = getConsentPreferences();
  
  // Check each category
  for (const [category, cookieNames] of Object.entries(COOKIE_MAPPINGS)) {
    if (cookieNames.includes(cookieName)) {
      return consent[category] === true;
    }
  }
  
  // If cookie is not categorized, default to necessary cookies only
  return false;
}